import { useQuery } from '@tanstack/react-query'
import type { VenueHistoryRow } from 'pages/Desk/venueHistory'
import { queryCustomVenueHistory } from 'supabase/desk/queryCustomVenueHistory'

// spec-desk-venue-location (33.3 D-02, D-10): the custom-venue history of the
// candidate's town, read once per town.
//
// The key is ['venueHistory', cityId, normalised cityText], deliberately
// OUTSIDE the 'desk' prefix (Pitfall P5): every desk decision and paste
// invalidates ['desk', ...], and setQueryDefaults(['desk']) turns focus and
// mount refetches on. History changes only when an event is saved, so a
// decision or a window focus must not re-read it.
//
// It is keyed by town only, not by candidate: one lookup serves every
// candidate in the same town, and a town change in the form is just a key
// change.

export const VENUE_HISTORY_QUERY_KEY = 'venueHistory'

/** Five minutes: long enough to serve a run of candidates in one town. */
const VENUE_HISTORY_STALE_TIME = 5 * 60 * 1000

/** One instance, so a consumer's useMemo on `rows` stays stable while there is no data. */
const NO_ROWS: VenueHistoryRow[] = []

export interface VenueHistoryTown {
  cityId: number | null
  cityText: string
}

export interface CustomVenueHistory {
  rows: VenueHistoryRow[]
  /** True while the RPC is fetching. */
  pending: boolean
  /** The lookup failed (D-10): the desk shows an inline note and keeps working. */
  failed: boolean
  /** The lookup is not needed (disabled, no town) or has finished, either way. */
  settled: boolean
}

/** Lower case, accents stripped, trimmed, whitespace collapsed: 'Sínes ' and 'sines' share a key. */
const normText = (text: string): string =>
  text
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ')

/**
 * Never throws (D-10, VLOC-08): an RPC error or a rejected request becomes
 * `{ rows: [], failed: true, settled: true }`. There is no `throwOnError`
 * and no retry, so a failing lookup costs one request and the desk keeps
 * working with no history.
 */
export const useCustomVenueHistory = (town: VenueHistoryTown, enabled: boolean): CustomVenueHistory => {
  const hasTown = town.cityId !== null || town.cityText.trim() !== ''
  const active = enabled && hasTown

  const query = useQuery<VenueHistoryRow[]>({
    queryKey: [VENUE_HISTORY_QUERY_KEY, town.cityId, normText(town.cityText)],
    queryFn: () => queryCustomVenueHistory(town),
    enabled: active,
    staleTime: VENUE_HISTORY_STALE_TIME,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  })

  // Disabled means "no history": a cached entry for the same town (another
  // candidate's lookup) must not leak into a listed-venue candidate's prefill
  // or baseline, since React Query hands cached data to disabled queries too.
  if (!active) {
    return { rows: NO_ROWS, pending: false, failed: false, settled: true }
  }

  return {
    rows: query.data ?? NO_ROWS,
    pending: query.fetchStatus === 'fetching',
    failed: query.isError,
    settled: query.isSuccess || query.isError
  }
}
